import { useState, useRef, useCallback } from "react";
/** TSD-drawer geometry domain: drawer open state, animation gate, resize height, drag ref, drag-start handler. */
export function useTsdDrawer() {
  const [tsDrawerOpen, setTsDrawerOpen] = useState(false);
  // Gates the height transition on the TSD chart container. We turn it on
  // only when we want the TSD chart's height to animate (closing the drawer
  // while in the lifted state transfers the drawer's height into the chart;
  // both the chart-grow and the wrapper-translate animations need to run in
  // sync over 0.28s). Off during normal handle drags so resizing feels live.
  const [tsdAnimating, setTsdAnimating] = useState(false);
  // Resizable TSD panel
  const [tsdH, setTsdH] = useState(0);
  const tsdDragRef = useRef(null);
  // Suppresses the parent wrapper's transform transition for a single frame.
  // Used by onTsdHandleMD to "absorb" the drawer-lift state into a real chart
  // height without sliding the handle down 25vh over 0.28s — see that handler
  // for the geometry.
  const [tsdSuppressTransform, setTsdSuppressTransform] = useState(false);
  // True while the TSD handle is being dragged. Used by the lifted-state
  // condition below to suppress the translateY(-25vh) lift mid-drag — without
  // this, the mousemove handler's 5% snap-to-0 would set tsdH=0 once the user
  // drags far enough down, the lifted condition (tsDrawerOpen && tsdH===0)
  // would re-engage, and the parent wrapper would transition back up to the
  // lifted position WHILE the user is still dragging. Symptom: the handle
  // "slides above the signal library" instead of behind it (the original bug
  // report). Re-engages naturally on mouseup if tsdH===0 with drawer open.
  const [tsdIsDragging, setTsdIsDragging] = useState(false);

  // TSD handle drag start.
  // When the indices drawer is open AND the chart is still collapsed (tsdH=0),
  // the parent wrapper is currently rendered with translateY(-25vh) so the
  // handle visually rides on top of the drawer. The first 1px of cursor
  // movement would flip the transform condition (tsdH===0 becomes false),
  // and the parent wrapper would animate translateY -25vh → 0 over 0.28s —
  // the handle would slide DOWN 25vh under the cursor, which is exactly the
  // "drag jumps erratically" symptom in the reported bug.
  // Fix: "absorb" the visual lift into a real chart height in a single frame.
  // translateY(-25vh)+height-0 and translateY(0)+height-25vh render at the
  // same Y, so as long as the transform transition is suppressed for that one
  // frame, the handle stays put under the cursor and the drag proceeds
  // smoothly from a 25vh baseline.
  const onTsdHandleMD = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      let baseH = tsdH;
      if (tsDrawerOpen && tsdH === 0) {
        baseH = Math.round(window.innerHeight * 0.25);
        setTsdSuppressTransform(true);
        setTsdH(baseH);
        // Re-enable the transform transition on the second animation frame,
        // by which point the new transform/height pair has already painted.
        requestAnimationFrame(() => requestAnimationFrame(() => setTsdSuppressTransform(false)));
      }
      setTsdIsDragging(true);
      tsdDragRef.current = { startY: e.clientY, startH: baseH };
    },
    [tsdH, tsDrawerOpen],
  );

  return {
    tsDrawerOpen,
    setTsDrawerOpen,
    tsdAnimating,
    setTsdAnimating,
    tsdH,
    setTsdH,
    tsdDragRef,
    tsdSuppressTransform,
    setTsdSuppressTransform,
    tsdIsDragging,
    setTsdIsDragging,
    onTsdHandleMD,
  };
}
